import { LoadState } from "@redotech/react-util/load";
import CheckCircleIcon from "@redotech/redo-icons/check-circle_filled.svg";
import RefreshIcon from "@redotech/redo-icons/refresh-cw-04_filled.svg";
import { isDef } from "@redotech/util/checks";
import {
  createContext,
  memo,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useState,
} from "react";
import { useDebounce } from "usehooks-ts";
import { Flex } from "../flex";
import { Text } from "../text";
import * as css from "./save-indicator.module.css";

export const SaveIndicator = memo(function SaveIndicator({
  isSaving,
  displayTextOnSaveSuccess,
}: {
  isSaving: boolean;
  displayTextOnSaveSuccess?: string;
}) {
  const SAVE_STATE_DURATION = 3000;

  const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout | undefined>(
    undefined,
  );
  const [previousIsSaving, setPreviousIsSaving] = useState(isSaving);
  const [saveState, setSaveState] = useState<SaveIndicatorState>(
    SaveIndicatorState.HIDDEN,
  );

  useEffect(() => {
    if (isSaving && !previousIsSaving) {
      setSaveState(SaveIndicatorState.SAVING);
      setPreviousIsSaving(isSaving);
      clearTimeout(timeoutId);
      setTimeoutId(undefined);
    } else if (!isSaving && previousIsSaving) {
      setSaveState(SaveIndicatorState.SAVED);
      setPreviousIsSaving(isSaving);
      setTimeoutId(
        setTimeout(() => {
          setSaveState(SaveIndicatorState.HIDDEN);
        }, SAVE_STATE_DURATION),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSaving]);

  return (
    <SaveIndicatorComponent
      displayTextOnSaveSuccess={displayTextOnSaveSuccess}
      state={saveState}
    />
  );
});

enum SaveIndicatorState {
  HIDDEN = "HIDDEN",
  SAVING = "SAVING",
  SAVED = "SAVED",
}

const SaveIndicatorComponent = memo(function SaveIndicator({
  state,
  displayTextOnSaveSuccess,
}: {
  state: SaveIndicatorState;
  displayTextOnSaveSuccess: string | undefined;
}) {
  switch (state) {
    case SaveIndicatorState.HIDDEN:
      return null;
    case SaveIndicatorState.SAVING:
      return <RefreshIcon className={css.savingIndicator} />;
    case SaveIndicatorState.SAVED:
      return (
        <Flex className={css.savedIndicator} gap="sm">
          <CheckCircleIcon className={css.icon} />
          {displayTextOnSaveSuccess && (
            <Text fontSize="xs" fontWeight="medium" textColor="tertiary">
              Saved
            </Text>
          )}
        </Flex>
      );
  }
});

interface SaveStatusContextValue {
  setIsSavingForId: (id: string, isSaving: boolean) => void;
  anySaving: boolean;
}

const SaveStatusContext = createContext<SaveStatusContextValue | undefined>(
  undefined,
);

/**
 * Tracks the saving state of any descendants that call `useRegisterIsSaving`,
 * so a single page-level indicator can reflect "any field on this page is
 * currently saving".
 */
export const SaveStatusProvider = memo(function SaveStatusProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [savingIds, setSavingIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  );

  const setIsSavingForId = useCallback((id: string, isSaving: boolean) => {
    setSavingIds((prev) => {
      const has = prev.has(id);
      if (isSaving === has) {
        return prev;
      }
      const next = new Set(prev);
      if (isSaving) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  }, []);

  const value = useMemo<SaveStatusContextValue>(
    () => ({ setIsSavingForId, anySaving: savingIds.size > 0 }),
    [setIsSavingForId, savingIds],
  );

  return (
    <SaveStatusContext.Provider value={value}>
      {children}
    </SaveStatusContext.Provider>
  );
});

/**
 * Drop-in replacement for `useState(false)` for tracking an auto-save's
 * `isSaving` flag. When rendered under a `SaveStatusProvider`, the saving
 * state is also reported to the provider so it can be aggregated across the
 * page. Outside a provider, it behaves as plain local state.
 */
export function useRegisterIsSaving(): [boolean, (isSaving: boolean) => void] {
  const id = useId();
  const ctx = useContext(SaveStatusContext);
  const setIsSavingForId = ctx?.setIsSavingForId;
  const [isSaving, setLocal] = useState(false);

  const setIsSaving = useCallback(
    (next: boolean) => {
      setLocal(next);
      setIsSavingForId?.(id, next);
    },
    [id, setIsSavingForId],
  );

  useEffect(() => {
    return () => setIsSavingForId?.(id, false);
  }, [id, setIsSavingForId]);

  return [isSaving, setIsSaving];
}

/**
 * Reports an externally-controlled `isSaving` boolean to the enclosing
 * `SaveStatusProvider`. Use this when a section already tracks its own
 * loading state (e.g. an imperative mutation handler) and you want that
 * state to participate in the aggregated page-level indicator. No-op
 * outside a provider.
 */
export function useReportIsSaving(isSaving: boolean): void {
  const id = useId();
  const ctx = useContext(SaveStatusContext);
  const setIsSavingForId = ctx?.setIsSavingForId;

  useEffect(() => {
    setIsSavingForId?.(id, isSaving);
    return () => setIsSavingForId?.(id, false);
  }, [id, isSaving, setIsSavingForId]);
}

/**
 * Returns `true` if any descendant under the nearest `SaveStatusProvider` is
 * currently saving. Returns `false` if there is no provider.
 */
export function useAggregateIsSaving(): boolean {
  return useContext(SaveStatusContext)?.anySaving ?? false;
}

/**
 * Handles debounced saving of a value without disrupting the ability of the
 * user to update the value locally.
 */
export function useAutoSave<T>({
  setIsSaving,
  isValidValueToSave,
  equals = (a, b) => a === b,
  debounceMs = 600,
  onSave,
  savedValueLoad,
  defaultValue,
}: {
  setIsSaving: (isSaving: boolean) => void;
  isValidValueToSave: (value: T) => boolean | Promise<boolean>;
  equals?: (a: T, b: T) => boolean;
  debounceMs?: number;
  onSave: (newValue: T) => Promise<void>;
  savedValueLoad: LoadState<T | undefined>;
  defaultValue: T;
}) {
  const [savedValue, setSavedValue] = useState<T>(defaultValue);
  const [newValue, setNewValue] = useState<T>(defaultValue);
  const debouncedNewValue = useDebounce(newValue, debounceMs);

  useEffect(() => {
    if (isDef(savedValueLoad.value)) {
      setSavedValue(savedValueLoad.value);
      setNewValue(savedValueLoad.value);
    }
  }, [savedValueLoad.value]);

  useEffect(() => {
    if (
      !savedValueLoad.pending &&
      isValidValueToSave(debouncedNewValue) &&
      !equals(debouncedNewValue, savedValue)
    ) {
      setIsSaving(true);
      void onSave(debouncedNewValue)
        .then(() => {
          setSavedValue(debouncedNewValue);
        })
        .finally(() => {
          setIsSaving(false);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedNewValue]);

  return { value: newValue, setValue: setNewValue };
}
